import { currentLocale, initialize, setKioskSlug, setLocale } from '@/app/i18n';
import { router } from '@/app/router';
import type { Overlay } from '@/app/stores/ui';
import { reloadWithCacheBust } from '@/services/DeploymentVersion';
import { idleWatcher } from '@/services/IdleWatcher';
import { kioskConfig } from '@/services/KioskConfig';
import { payment } from '@/services/Payment';
import { printing } from '@/services/Printing';
import { sentryService } from '@/services/telemetry/sentry';
import type {
  AppBoundHandlers,
  AppKioskBootstrapRuntime,
  AppLocaleChange,
  AppUiStore,
} from './appRuntimeTypes';

interface UseKioskBootstrapOptions {
  boundHandlers: AppBoundHandlers;
  ui: Pick<AppUiStore, 'createLoadingIndicator'>;
}

function reportError(...args: unknown[]): void {
  // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
  console.error(...args);
}

function warn(...args: unknown[]): void {
  // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
  console.warn(...args);
}

export function useKioskBootstrap({
  boundHandlers,
  ui,
}: UseKioskBootstrapOptions): AppKioskBootstrapRuntime {
  function initializeKioskServices(): void {
    const loadingIndicator: Overlay = ui.createLoadingIndicator();

    kioskConfig
      .load(router.slug)
      .then(async ({ kiosk }) => {
        try {
          sentryService.endSentrySession('ok');
          sentryService.setTag('kiosk_session_id', undefined);
        } catch {
          warn('[App] Failed to end boot Sentry session');
        }

        idleWatcher.initialize();
        printing.trackPrinterContext();
        payment.initialize();

        setKioskSlug(kiosk.slug);
        initialize(router.localeCode, kiosk.languages);
        boundHandlers.localeChanged = (change: AppLocaleChange): void => {
          setLocale(change.to);
        };
        router.on(router.EVENTS.LOCALE_CHANGED, boundHandlers.localeChanged);

        const initializedLocale = currentLocale.value as { code: string };
        await router.redirectToLocale(initializedLocale.code, { silent: true });

        kioskConfig.startAutoRefresh();
      })
      .catch((error: unknown) => {
        reportError('[App] Kiosk service initialization failed:', error);

        try {
          sentryService.endSentrySession('crashed');
          sentryService.setTag('kiosk_session_id', undefined);
        } catch {
          scheduleReloadAfterBootFailure();
          return;
        }

        scheduleReloadAfterBootFailure();
      })
      .finally(() => {
        loadingIndicator.hide();
      });
  }

  function scheduleReloadAfterBootFailure(): void {
    reportError('[App] Scheduling auto-reload in 15s due to boot failure');
    setTimeout(() => {
      try {
        sentryService.flush(2000);
      } catch {
        reloadWithCacheBust();
        return;
      }
      reloadWithCacheBust();
    }, 15_000);
  }

  return { initializeKioskServices };
}
